import React from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import DataGrid, { DataGridItem } from "components/data-grid";
import { FormattedMessage, injectIntl } from "react-intl";
import styles from "./styles/livestream-data.css.js";
import withStyles from "components/with-styles";
import { TextDisplay } from "components/display-data";
import { PageSection } from "components/page";
import { ExternalLink } from "components/link";
import Chip from "@material-ui/core/Chip";
import RTMPActionButton, { rtmpStartMutation, rtmpStopMutation, rtmpDeleteMutation } from "./partial";
import { DeleteActionWithModal } from "components/buttons";
import { useMutation } from "@apollo/react-hooks";
import { eventStatusTypes } from "pages/event/utils";
import FormHelperText from "@material-ui/core/FormHelperText";

const availableStatuses = {
  RUNNING: "running",
  STOPPED: "stopped",
  DELETED: "deleted",
};

const normalizeStatus = status => (Object.values(availableStatuses).includes(status) ? status : "intermediate");

const LiveStreamData = ({ liveStream, classes, intl, onAction, event }) => {
  const [rtmpDelete] = useMutation(rtmpDeleteMutation);
  const isEventStarted = event?.eventStatus === eventStatusTypes.STARTED;
  const isLivestreamDeleted = isEventStarted && liveStream.componentStatus === availableStatuses.DELETED;

  return (
    <PageSection
      className={cn(classes.liveStream, {
        [classes.deleted]: isLivestreamDeleted,
      })}
      key={liveStream.key}
      subsection
    >
      <DataGrid>
        <DataGridItem big className={classes.livestream_actions_container}>
          {isEventStarted && liveStream.componentStatus === availableStatuses.STOPPED && (
            <DeleteActionWithModal
              showLoading={false}
              className={cn(classes.livestream__actions, {
                [classes.deleted__text]: isLivestreamDeleted,
              })}
              mutation={rtmpDelete}
              mutationVariables={{
                eventId: event.id,
                componentName: liveStream.componentName,
              }}
              elementNameInResult="deleteRTMP"
              elementToDelete={liveStream.rtmpUrl}
              onSuccess={() => onAction(true)}
              onError={() => onAction(false)}
            />
          )}
          {isEventStarted && liveStream.componentStatus === availableStatuses.RUNNING && (
            <RTMPActionButton
              icon="stop"
              componentName={liveStream.componentName}
              className={cn(classes.livestream__actions, {
                [classes.deleted__text]: isLivestreamDeleted,
              })}
              eventId={event.id}
              actionMutation={rtmpStopMutation}
              onAction={onAction}
            />
          )}
          {isEventStarted && liveStream.componentStatus === availableStatuses.STOPPED && (
            <RTMPActionButton
              icon="play-arrow"
              componentName={liveStream.componentName}
              className={cn(classes.livestream__actions, {
                [classes.deleted__text]: isLivestreamDeleted,
              })}
              eventId={event.id}
              actionMutation={rtmpStartMutation}
              onAction={onAction}
            />
          )}
          {liveStream.componentStatus && (
            <Chip
              className={cn(classes.livestream__status, {
                [classes.deleted__text]: isLivestreamDeleted,
                [classes.deleted__status]: isLivestreamDeleted,
              })}
              variant="outlined"
              label={intl.formatMessage({ id: `event.rtmp.status.in.${normalizeStatus(liveStream.componentStatus)}` })}
            />
          )}
        </DataGridItem>

        <DataGridItem>
          <TextDisplay
            label={intl.formatMessage({ id: "common.description" })}
            className={cn(classes.data, {
              [classes.deleted__text]: isLivestreamDeleted,
            })}
            classNameLabel={cn({
              [classes.deleted__text]: isLivestreamDeleted,
            })}
          >
            <span>{liveStream.description}</span>
          </TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay
            label={intl.formatMessage({ id: "event.liveStream.key" })}
            className={cn(classes.data, {
              [classes.deleted__text]: isLivestreamDeleted,
            })}
            classNameLabel={cn({
              [classes.deleted__text]: isLivestreamDeleted,
            })}
          >
            {liveStream.key}
          </TextDisplay>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay
            label={intl.formatMessage({ id: "event.liveStream.rtmpUrl" })}
            className={cn(classes.data, {
              [classes.deleted__text]: isLivestreamDeleted,
            })}
            classNameLabel={cn({
              [classes.deleted__text]: isLivestreamDeleted,
            })}
          >
            {liveStream.rtmpUrl}
          </TextDisplay>
          <FormHelperText>
            <FormattedMessage
              id="event.liveStream.rtmpUrl.info"
              values={{ rtmpUrl: liveStream.rtmpUrl, key: liveStream.key }}
            />
          </FormHelperText>
        </DataGridItem>
        <DataGridItem>
          <TextDisplay
            label={intl.formatMessage({ id: "event.liveStream.publishingUrl" })}
            className={cn(classes.data, {
              [classes.deleted__text]: isLivestreamDeleted,
            })}
            classNameLabel={cn({
              [classes.deleted__text]: isLivestreamDeleted,
            })}
          >
            {isLivestreamDeleted ? (
              liveStream.publishingUrl
            ) : (
              <ExternalLink href={liveStream.publishingUrl}>{liveStream.publishingUrl}</ExternalLink>
            )}
          </TextDisplay>
        </DataGridItem>
      </DataGrid>
    </PageSection>
  );
};

LiveStreamData.propTypes = {
  /**
   * Datos a representar
   **/
  liveStream: PropTypes.object,
  /**
   * Evento al que pertenece el liveStream
   **/
  event: PropTypes.object,
  /**
   * Callback tras realizar una acción del livestream
   **/
  onAction: PropTypes.func,
};

export default withStyles(styles)(injectIntl(LiveStreamData));
